// hooks/useAuth2FASelect.ts
import { useSelector } from 'react-redux';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAppDispatch } from '@/store';
import { selectAuthError, selectAuthStatus, selectMaskedEmail, selectMaskedPhone, selectTwoFactorToken } from '@/store/selectors/auth';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import type { TwoFactorMethod } from '@/types/auth/Auth';
import { clearLoginError } from '@/store/slices/auth/authSlice';

/**
 * Hook for /login/2fa/select route
 * Handles 2FA method selection
 */
export function useAuth2FASelect() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const twoFactorToken = useSelector(selectTwoFactorToken);
  const maskedEmail = useSelector(selectMaskedEmail);
  const maskedPhone = useSelector(selectMaskedPhone);

  const isLoading = authStatus === 'loading';

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  // TODO Move to route guard
  useEffect(() => {
    if (twoFactorToken === undefined) {
      void navigate({ to: '/login' });
    }
  }, [twoFactorToken, navigate]);

  const selectMethod = async(method: TwoFactorMethod) => {
    if (twoFactorToken === undefined) {
      await navigate({ to: '/login' });
      return;
    }

    const action = await dispatch(select2FAMethodThunk({ method }));
    if (select2FAMethodThunk.fulfilled.match(action)) {
      await navigate({ to: '/login/2fa/verify' });
    }
  };

  return {
    state: { isLoading, error, maskedEmail, maskedPhone },
    handlers: { selectMethod },
  };
}
